import { useTranslation } from 'react-i18next';
import Agreement from 'components/Assessment/Agreement';
import { AsscessmentTarget, ASSESSMENT_SUBJECT_SELF, ASSESSMENT_SUBJECT_OTHERS, getSubjectName } from 'components/Assessment/Subject';
import { AssessmentAge } from 'components/Assessment/Age';
import { AssessmentHealthStatus, getHealthStatusName } from 'components/Assessment/HealthStatus';
import { AssessmentConsiderations } from 'components/Assessment/Considerations';
import { AssessmentWorries } from 'components/Assessment/Worries';
import { AssessmentDiscussWithOthers } from 'components/Assessment/DiscussWithOthers';
import { Results } from 'components/Assessment/Results';
import { useSignal, useSignalEffect, useComputed } from "@preact/signals-react";
import 'components/Assessment.scss';

// Specify the key for local storage
const LOCALSTORAGE_KEY = 'cc-self-assessment';
// Specify the steps
const STEP_AGREEMENT = 0;
const STEP_ASSESSMENT_SUBJECT = 1;
const STEP_ASSESSMENT_AGE = 2;
const STEP_ASSESSMENT_HEALTH = 3;
const STEP_ASSESSMENT_CONSIDERATIONS = 4;
const STEP_ASSESSMENT_WORRIES = 5;
const STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS = 6;
const STEP_END = 7;
const DEFAULT_DATA = {
  version: 1,
  submissionId: null,
  language: null,
  step: STEP_AGREEMENT,
  agreement: true,
  assessmentSubject: null,
  age: null,
  healthStatus: null,
  considerations: [],
  hasWorries: null,
  worries: null,
  discussedWithOthers: null,
};

/**
 * Get human readable name for the step
 *
 * @param {Number} step Integer step number
 * @returns {string}
 */
function getStepName(step) {
  switch (step) {
    case STEP_AGREEMENT:
      return 'agreement';
    case STEP_ASSESSMENT_SUBJECT:
      return 'step 1: assessmentSubject';
    case STEP_ASSESSMENT_AGE:
      return 'step 2: age';
    case STEP_ASSESSMENT_HEALTH:
      return 'step 3: healthStatus';
    case STEP_ASSESSMENT_CONSIDERATIONS:
      return 'step 4: considerations';
    case STEP_ASSESSMENT_WORRIES:
      return 'step 5: worries';
    case STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS:
      return 'step 6: discussed with others';
    case STEP_END:
      return 'finish and read results';
    default:
      return 'unknown';
  }
}

/**
 * Generate a random string for browser / submission identification
 *
 * @param {Number} l Integer length of the string to generate
 * @returns {string} The randomly generated string
 */
function generateRandomString(l=20) {
  const s = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: l }, () => s[Math.floor(Math.random() * s.length)]).join('');
}

/**
 * Save the data to local storage
 *
 * @param {object} data
 * @returns {void}
 */
function saveData(data) {
  if (window.localStorage) {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(data));
  }
}

/**
 * Load the data from local storage (or load the default data)
 *
 * @returns {object}
 */
function loadData() {
  if (!window.localStorage) {
    return DEFAULT_DATA;
  }

  const dataStr = window.localStorage.getItem(LOCALSTORAGE_KEY);
  if (dataStr === null) {
    return DEFAULT_DATA;
  }

  const data = JSON.parse(dataStr);
  if (typeof data !== 'object') {
    return DEFAULT_DATA;
  }

  return {
    ...DEFAULT_DATA,
    ...JSON.parse(dataStr),
  }
}

/**
 * Clear the saved data from local storage
 *
 * @returns {boolean} Returns if local storage is presents or not.
 */
function clearSavedData() {
  if (window.localStorage) {
    window.localStorage.removeItem(LOCALSTORAGE_KEY);
    return true;
  }
  return false;
}

/**
 * Get the browser ID from local storage or generate a new one.
 * For identifying a unique browser without using cookies or tracking
 * browser signatures (e.g. user agent string, IP address).
 *
 * @param {Number} l Integer length of the browser ID to generate
 * @returns {string}
 */
function getBrowserId(l=20) {
  const key = 'cc-self-assessment__browser-id';
  if (window && window.localStorage) {
    let browserId = window.localStorage.getItem(key);
    if (browserId === null) {
      browserId = generateRandomString(l);
      window.localStorage.setItem(key, browserId);
    }
    return browserId;
  }
  return 'not-a-browser';
}

const LOCALE_DEFAULT='zh-hant';

/**
 *
 * @param {Object} attributes
 * @param {HTMLElement|null} attributes.element
 *
 * @returns
 */
function Assessment({ element=null }) {

  /**
   * Clear saved data and reload.
   * Will ask for confirmation before clearing the data (when in browser).
   *
   * @returns {void}
   */
  function clearSavedDataAndReload() {
    if (window && !window.confirm(t("confirm to clear all data"))) {
      return;
    }
    clearSavedData();
    if (window) {
      window.location.reload(); // reload page
    }
  }

  /**
   * Helper function to submit data to server
   */
  function submitData() {
    console.debug('to determine if to submit data to server: ', dataSubmitUrl, agreement.value);
    if (dataSubmitUrl && agreement.value === true) {
      const status = getStepName(step.value);

      // User agree to submit data to server
      console.debug('to submit data to server: ', dataSubmitUrl);
      (async function postData(url = '', data = {}, context={}) {
        console.debug('to submit data', data);
        // Default options are marked with *
        const response = await fetch(url, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          redirect: 'follow',
          referrerPolicy: 'no-referrer',
          body: JSON.stringify(data),
        });
        // TODO: tell user if the submission fail? Or log somewhere?
        return response.json();
      })(
        dataSubmitUrl,
        {
          browserId,
          submissionId,
          language: language.value,
          agreement: agreement.value,
          assessmentSubject: getSubjectName(assessmentSubject.value),
          age: age.value,
          healthStatus: getHealthStatusName(healthStatus.value),
          considerationsAvailable,
          considerations: considerations.value,
          hasWorries: hasWorries.value,
          worries: worries.value,
          discussedWithOthers: discussedWithOthers.value,
          status,
        },
      );
    } else {
      console.debug('will not submit data to server: ', dataSubmitUrl, agreement.value);
    }
  }

  // Get the locale from the element if available.
  const locale = element?.dataset?.locale || LOCALE_DEFAULT;
  const considerationsAvailable = (function (text) {
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        throw new Error('invalid data');
      }
      if (parsed.length === 0) {
        throw new Error('empty data');
      }

      // Check if there is invalid data
      const invalids = parsed.filter((item) => (!(typeof item === 'object' && item.id && item.title)))
      if (invalids.length > 0) {
        throw new Error('invalid items within parsed data', invalids);
      }

      return parsed;
    } catch (e) {
      console.debug('considerationsAvailable', 'parse error', e, text);
      return [];
    }
  })(element?.dataset?.considerationsAvailable || null);
  const dataSubmitUrl = element?.dataset?.submitUrl || null;

  // Get the translation function.
  const { t, i18n } = useTranslation();
  i18n.changeLanguage(locale.replace('-', '_'));

  // TODO: load previous data from localStorage, if any, if not expired
  const previousData = loadData();

  // Create signals for display.
  const step = useSignal(previousData.step || DEFAULT_DATA.step);
  const language = useSignal(locale || previousData.language || DEFAULT_DATA.language);
  const agreement = useSignal(previousData.agreement || DEFAULT_DATA.agreement);
  const assessmentSubject = useSignal(previousData.assessmentSubject || DEFAULT_DATA.assessmentSubject);
  const age = useSignal(previousData.age || DEFAULT_DATA.age);
  const healthStatus = useSignal(previousData.healthStatus || DEFAULT_DATA.healthStatus);
  const considerations = useSignal(previousData.considerations || DEFAULT_DATA.considerations);
  const hasWorries = useSignal(previousData.hasWorries || DEFAULT_DATA.hasWorries);
  const worries = useSignal(previousData.worries || DEFAULT_DATA.worries);
  const discussedWithOthers = useSignal(previousData.discussedWithOthers || DEFAULT_DATA.discussedWithOthers);

  // Generate a submission ID for data submission.
  const submissionId = previousData.submissionId || generateRandomString();
  const browserId = getBrowserId();

  // Store the changes if any of the signal values have changed.
  useSignalEffect(() => {
    const stepData = {
      submissionId,
      browserId,
      language: language.value,
      step: step.value,
      agreement: agreement.value,
      assessmentSubject: assessmentSubject.value,
      age: age.value,
      healthStatus: healthStatus.value,
      considerations: considerations.value,
      hasWorries: hasWorries.value,
      worries: worries.value,
      discussedWithOthers: discussedWithOthers.value,
    };
    saveData(stepData);
    console.debug('step', step.value, 'stepData', stepData);
  });

  // Derived display values from signals.
  const noun = useComputed(() => {
    switch (assessmentSubject.value) {
      case ASSESSMENT_SUBJECT_SELF:
        return '你';
      case ASSESSMENT_SUBJECT_OTHERS:
        return '對方';
      default:
        return '對象';
    }
  });

  // Create validator values
  const VALIDATE_OK = { valid: true, message: '' };
  const validateAgreement = useComputed(() => (VALIDATE_OK));
  const validateTarget = useComputed(() => (
    (assessmentSubject.value !== null)
      ? VALIDATE_OK
      : { valid: false, message: '請將評估對象是誰告訴測試程式' }
  ));
  const validateAge = useComputed(() => (
    (age.value !== null && typeof(age.value) === 'number' && !age.value.isNaN && age.value > 0)
      ? VALIDATE_OK
      : { valid: false, message: `請將${noun.value}的年齡告訴測試程式` }
  ));
  const validateHealthStatus = useComputed(() => (
    (healthStatus.value !== null)
      ? VALIDATE_OK
      : { valid: false, message: `請將${noun.value}的健康狀況告訴測試程式` }
  ));
  const validateConsiderations = useComputed(() => (VALIDATE_OK));
  const validateWorries = useComputed(() => (
    (hasWorries.value === null) ? { valid: false, message: '請告訴測試程式是否有擔憂' }
    : (hasWorries.value === true && (worries.value === null || worries.value.trim() === '')) ? { valid: false, message: '請告訴測試程式擔憂的事情' }
    : VALIDATE_OK
  ));
  const validateDiscussedWithOthers = useComputed(() => (
    (discussedWithOthers.value !== null)
      ? VALIDATE_OK
      : { valid: false, message: '請告訴測試程式是否與他人討論過' }
  ));

  const navigation = useComputed(() => {
    switch (step.value) {
      case STEP_AGREEMENT:
        {
          const disabledNext = !validateAgreement.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_SUBJECT; submitData()}} disabled={disabledNext}>{t('start')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_SUBJECT:
        {
          const disabledNext = !validateTarget.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_AGREEMENT}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_ASSESSMENT_AGE; submitData()}} disabled={disabledNext}>{t('next')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_AGE:
        {
          const disabledNext = !validateAge.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_SUBJECT}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_ASSESSMENT_HEALTH; submitData()}} disabled={disabledNext}>{t('next')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_HEALTH:
        {
          const disabledNext = !validateHealthStatus.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_AGE}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_ASSESSMENT_CONSIDERATIONS; submitData()}} disabled={disabledNext}>{t('next')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_CONSIDERATIONS:
        {
          const disabledNext = !validateConsiderations.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_HEALTH}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_ASSESSMENT_WORRIES; submitData()}} disabled={disabledNext}>{t('next')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_WORRIES:
        {
          const disabledNext = !validateWorries.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_CONSIDERATIONS}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS; submitData()}} disabled={disabledNext}>{t('next')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS:
        {
          const disabledNext = !validateDiscussedWithOthers.value.valid;
          return (
            <>
              <button onClick={() => {step.value = STEP_ASSESSMENT_WORRIES}}>{t('prev')}</button>
              <button onClick={() => {step.value = STEP_END; submitData()}} disabled={disabledNext}>{t('see result')}</button>
              <button onClick={() => {clearSavedDataAndReload()}}>{t('clear all data')}</button>
            </>
          );
        }
      case STEP_END:
        return (
          <>
            <button onClick={() => {clearSavedDataAndReload()}}>
              {t('clear all data and restart')}
            </button>
          </>
        );
      default:
        console.error('Invalid step', step.value);
        return null;
    }
  });

  // Derive which slide to show by the signals.
  const slideClassName = "cc-self-assessment__assessment__question";
  const slide = useComputed(() => {
    switch (step.value) {
      case STEP_AGREEMENT:
        return (
          <Agreement
            className={slideClassName}
            defaultValue={agreement.value}
            onAnswer={(agreed) => {
              agreement.value = (agreed === true);
            }}
          />
        );
      case STEP_ASSESSMENT_SUBJECT:
        return (
          <AsscessmentTarget
            className={slideClassName}
            defaultOption={assessmentSubject.value}
            onAnswer={(ans) => {
              assessmentSubject.value = ans;
            }}
          />
        );
      case STEP_ASSESSMENT_AGE:
        return (
          <AssessmentAge
            className={slideClassName}
            defaultValue={age.value}
            assessmentSubject={assessmentSubject}
            onAnswer={(ans) => {
              age.value = ans;
            }}
            onNext={() => {
              if (!validateAge.value.valid) {
                // TODO: show some message?
                return;
              }
              step.value = STEP_ASSESSMENT_HEALTH;
              submitData();
            }}
          />
        );
      case STEP_ASSESSMENT_HEALTH:
        return (
          <AssessmentHealthStatus
            className={slideClassName}
            defaultOption={healthStatus.value}
            assessmentSubject={assessmentSubject}
            onAnswer={(ans) => {
              healthStatus.value = ans;
            }}
          />
        );
      case STEP_ASSESSMENT_CONSIDERATIONS:
        return (
          <AssessmentConsiderations
            className={slideClassName}
            assessmentSubject={assessmentSubject}
            availableValues={considerationsAvailable}
            defaultValues={considerations.value}
            onAnswer={(ans) => {
              considerations.value = ans;
            }}
          />
        );
      case STEP_ASSESSMENT_WORRIES:
        return (
          <AssessmentWorries
            className={slideClassName}
            defaultOption={hasWorries.value}
            defaultText={worries.value}
            assessmentSubject={assessmentSubject}
            onAnswer={(ans) => {
              hasWorries.value = ans.hasWorries;
              worries.value = ans.worries;
            }}
            onNext={() => {
              if (!validateAge.value.valid) {
                // TODO: show some message?
                return;
              }
              step.value = STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS;
              submitData();
            }}
          />
        );
      case STEP_ASSESSMENT_DISCUSSED_WITH_OTHERS:
        return (
          <AssessmentDiscussWithOthers
            className={slideClassName}
            defaultOption={discussedWithOthers.value}
            assessmentSubject={assessmentSubject}
            onAnswer={(ans) => {
              discussedWithOthers.value = ans;
            }}
          />
        );
      default:
        clearSavedData();
        return (
          <Results
            assessmentConsiderationsAvailable={considerationsAvailable}
            {...{
              language,
              agreement,
              assessmentSubject,
              age,
              healthStatus,
              considerations,
              hasWorries,
              worries,
              discussedWithOthers,
            }}
          />
        );
    }
  });
  return (
    <div className="cc-self-assessment">
      <div className="cc-self-assessment__assessment">
        {slide}
      </div>
      <div className="cc-self-assessment__navigation">
        {navigation}
      </div>
    </div>
  );
}

export default Assessment;
